import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CHAT_LIST_PANEL_WIDTH_PX } from 'layout/MainLayout/layout-zoom-constants';
import { dispatch, store } from 'store';

export interface SidebarParams {
  open: boolean;
  width?: number;
  activeId: string | null;
  title?: string;
  hideHeader?: boolean;
  onClose?: () => void;
  /** פאדינג/כותרת כמו פאנל רשימת השיחות (צ'אט) */
  chatPanelStyle?: boolean;
}

export interface DualSidebarState {
  left: SidebarParams;
  right: SidebarParams;
}

/** ברירת מחדל לרוחב מגירות dual sidebar (קטלוג / הגדרות בבוט בילדר וכו') — תואם ל־initial state */
export const DEFAULT_SIDEBAR_PANEL_WIDTH = 560;

const initialSidebarState: SidebarParams = {
  open: false,
  width: DEFAULT_SIDEBAR_PANEL_WIDTH,
  activeId: null,
  title: '',
  hideHeader: false,
  onClose: undefined,
  chatPanelStyle: false,
};

const initialState: DualSidebarState = {
  left: { ...initialSidebarState },
  right: { ...initialSidebarState, width: CHAT_LIST_PANEL_WIDTH_PX },
};

const dualSidebarSlice = createSlice({
  name: 'dualSidebar',
  initialState,
  reducers: {
    setLeftSidebar(state, action: PayloadAction<Partial<SidebarParams>>) {
      state.left = { ...state.left, ...action.payload };
      // אם פותחים שמאלי, סוגרים ימני
      if (action.payload.open) {
        state.right.open = false;
      }
    },
    setRightSidebar(state, action: PayloadAction<Partial<SidebarParams>>) {
      const p = action.payload;
      state.right = {
        ...state.right,
        ...p,
        // ברים שלא מעבירים chatPanelStyle לא יורשים true מהבר הקודם
        ...(p.open === true && p.chatPanelStyle === undefined ? { chatPanelStyle: false } : {}),
      };
      if (p.open) {
        state.left.open = false;
      }
    },
    clearLeftSidebar(state) {
      state.left = { ...initialSidebarState };
    },
    clearRightSidebar(state) {
      state.right = { ...initialSidebarState, width: CHAT_LIST_PANEL_WIDTH_PX, chatPanelStyle: false };
    },
    closeAllSidebars(state) {
      state.left.open = false;
      state.right.open = false;
    },
  },
});

export const { 
  setLeftSidebar, 
  setRightSidebar, 
  clearLeftSidebar, 
  clearRightSidebar, 
  closeAllSidebars 
} = dualSidebarSlice.actions;

export default dualSidebarSlice.reducer;

// פונקציות עזר
export const openLeftSidebar = (id: string, params?: Partial<Omit<SidebarParams, 'activeId' | 'open'>>) => {
  dispatch(setLeftSidebar({ 
    open: true, 
    activeId: id, 
    ...params 
  }));
};

export const openRightSidebar = (id: string, params?: Partial<Omit<SidebarParams, 'activeId' | 'open'>>) => {
  dispatch(setRightSidebar({ 
    open: true, 
    activeId: id, 
    ...params 
  }));
};

export const closeLeftSidebar = () => {
  const activeIdWhenClosing = store.getState().dualSidebar.left.activeId;
  dispatch(setLeftSidebar({ open: false }));
  setTimeout(() => {
    // Only clear if no one else opened a new sidebar in the meantime
    const currentActiveId = store.getState().dualSidebar.left.activeId;
    if (currentActiveId === activeIdWhenClosing || !currentActiveId) {
      dispatch(clearLeftSidebar());
    }
  }, 500);
};

export const closeRightSidebar = () => {
  const activeIdWhenClosing = store.getState().dualSidebar.right.activeId;
  dispatch(setRightSidebar({ open: false }));
  setTimeout(() => {
    // Only clear if no one else opened a new sidebar in the meantime
    const currentActiveId = store.getState().dualSidebar.right.activeId;
    if (currentActiveId === activeIdWhenClosing || !currentActiveId) {
      dispatch(clearRightSidebar());
    }
  }, 500);
};

export const closeBothSidebars = () => {
  dispatch(closeAllSidebars());
  setTimeout(() => {
    dispatch(clearLeftSidebar());
    dispatch(clearRightSidebar());
  }, 500);
}; 